import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '~/shared/shadcn/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '~/shared/shadcn/ui/form';
import { Input } from '~/shared/shadcn/ui/input';
import { Button } from '~/shared/shadcn/ui/button';
import { Textarea } from '~/shared/shadcn/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/shared/shadcn/ui/select';
import { useCreateVolunteer, useUpdateVolunteer } from '../api/hooks/useVolunteers';
import type { CreateVolunteerDto, RecitalVolunteer } from '../model/types/volunteers';

const volunteerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Invalid email address'),
  relation: z.enum(['father', 'mother', 'student', 'guardian', 'other'], {
    required_error: 'Relation is required',
  }),
  role: z.string().min(1, 'Role is required'),
  showId: z.string().optional(),
  notes: z.string().optional(),
});

type VolunteerFormData = z.infer<typeof volunteerSchema>;

interface VolunteerSignupFormProps {
  recitalId: string;
  shows?: Array<{ _id: string; name: string }>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  volunteer?: RecitalVolunteer | null; // For edit mode
}

const VOLUNTEER_ROLES = [
  'Backstage Helper',
  'Usher',
  'Ticket Sales',
  'Setup/Cleanup',
  'Concessions',
  'Stage Manager',
  'Other',
];

export function VolunteerSignupForm({
  recitalId,
  shows = [],
  open,
  onOpenChange,
  onSuccess,
  volunteer,
}: VolunteerSignupFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createVolunteer = useCreateVolunteer();
  const updateVolunteer = useUpdateVolunteer();

  const isEditMode = !!volunteer;

  const form = useForm<VolunteerFormData>({
    resolver: zodResolver(volunteerSchema),
    defaultValues: {
      name: volunteer?.name || '',
      email: volunteer?.email || '',
      relation: volunteer?.relation || undefined,
      role: volunteer?.role || '',
      showId: typeof volunteer?.showId === 'object' ? volunteer.showId._id : volunteer?.showId || undefined,
      notes: volunteer?.notes || '',
    },
  });

  const onSubmit = async (data: VolunteerFormData) => {
    setIsSubmitting(true);
    try {
      const sanitizedShowId =
        data.showId && data.showId !== 'any' ? data.showId : undefined;

      const volunteerData: CreateVolunteerDto = {
        name: data.name,
        email: data.email,
        relation: data.relation,
        role: data.role,
        showId: sanitizedShowId,
        notes: data.notes,
      };

      if (isEditMode && volunteer) {
        // Update existing volunteer
        await updateVolunteer.mutateAsync({
          volunteerId: volunteer._id,
          data: volunteerData,
        });
      } else {
        // Create new volunteer
        await createVolunteer.mutateAsync({
          recitalId,
          data: volunteerData,
        });
      }

      form.reset();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      console.error(`Failed to ${isEditMode ? 'update' : 'sign up'} volunteer:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Volunteer' : 'Sign Up as Volunteer'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Update volunteer information and assignment details.'
              : 'Help make this recital a success by volunteering your time!'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter your full name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email Address *</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="your.email@example.com" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="relation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Your Relation to Student *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select your relation" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="father">Father</SelectItem>
                      <SelectItem value="mother">Mother</SelectItem>
                      <SelectItem value="student">Student</SelectItem>
                      <SelectItem value="guardian">Guardian</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="role"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Volunteer Role *</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a role" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {VOLUNTEER_ROLES.map((role) => (
                        <SelectItem key={role} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {shows.length > 0 && (
              <FormField
                control={form.control}
                name="showId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Specific Show (Optional)</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Any show" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="any">Any Show</SelectItem>
                        {shows.map((show) => (
                          <SelectItem key={show._id} value={show._id}>
                            {show.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Additional Notes (Optional)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Any special skills, preferences, or availability details..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Submitting...' : 'Sign Up as Volunteer'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
